// Which key presses which card action, and what one press does. Configured actions (the model's,
// at most 3) take 1/2/3 in order; the runtime-injected local actions keep their fixed keys
// (dismiss d, snooze h, archive a); `s` is the card's send action. Reversible mailbox changes
// (archive, move back, labels) run on one press with Undo; irreversible external actions (sends,
// calendar writes) only ARM on a press, and a second, separate press confirms (decided 2026-10-12,
// Chris; the kinds are in shared/actionKinds.ts). Either way the server binds the same approval.
import { isReversibleActionKind } from "@shared/actionKinds";
import type { CardAction } from "@shared/core";

/** Mirrors RESERVED_ACTION_IDS in shared/core.ts (kept local so the web bundle stays zod-free). */
export const INJECTED_ACTION_IDS = ["dismiss", "snooze", "task", "default-cleanup"] as const;
const INJECTED = new Set<string>(INJECTED_ACTION_IDS);

export const INJECTED_KEYS: Record<string, string> = {
  dismiss: "d",
  snooze: "h",
  "default-cleanup": "a",
};

export interface KeyedAction {
  action: CardAction;
  /** Key shown on the button and bound while the card is active ("1", "d", …); null if none. */
  key: string | null;
  injected: boolean;
}

export function isInjected(action: Pick<CardAction, "id">): boolean {
  return INJECTED.has(action.id);
}

/** A second press within this window is treated as a double-tap, not a confirmation. */
export const CONFIRM_GUARD_MS = 250;

/** Actions that change something outside MeeMail: the gateway approves and runs them. */
export function isExternal(action: Pick<CardAction, "behavior">): boolean {
  return action.behavior === "approve_action" || action.behavior === "default_cleanup";
}

/** External actions that cannot be taken back, and therefore need arm → confirm. */
export function needsConfirmation(action: Pick<CardAction, "behavior" | "actionKind">): boolean {
  return isExternal(action) && !isReversibleActionKind(action.actionKind);
}

/**
 * What one press of an action does: `run` it now (local actions and reversible mailbox changes,
 * which Undo takes back), `arm` an irreversible one, `confirm` the one already armed, or `wait`
 * when the same action is pressed again inside the double-tap guard.
 */
export type PressStep = "run" | "arm" | "confirm" | "wait";

export function pressStep(
  action: Pick<CardAction, "id" | "behavior" | "actionKind">,
  armed: { actionId: string; at: number } | null,
  now: number,
): PressStep {
  if (!needsConfirmation(action)) return "run";
  if (armed?.actionId !== action.id) return "arm";
  return now - armed.at < CONFIRM_GUARD_MS ? "wait" : "confirm";
}

export function keyedActions(actions: readonly CardAction[]): KeyedAction[] {
  const configured = actions.filter((a) => !isInjected(a)).slice(0, 3);
  const injected = actions.filter((a) => isInjected(a));
  return [
    ...configured.map((action, i) => ({ action, key: String(i + 1), injected: false })),
    ...injected.map((action) => ({
      action,
      key: INJECTED_KEYS[action.id] ?? null,
      injected: true,
    })),
  ];
}

/** The action a key press means on this card (digits and injected keys). */
export function actionForKey(actions: readonly CardAction[], key: string): CardAction | null {
  return keyedActions(actions).find((k) => k.key === key)?.action ?? null;
}

/** `s`: the card's send (reply or forward) action, if it has one. */
export function sendAction(actions: readonly CardAction[]): CardAction | null {
  return (
    actions.find(
      (a) =>
        !isInjected(a) &&
        a.behavior === "approve_action" &&
        (a.actionKind === "gmail.send_reply" || a.actionKind === "gmail.forward"),
    ) ?? null
  );
}

/** Label for the "Next thing" line in the footer. */
export function nextThingLabel(nextThing: string | null, actions: readonly CardAction[]): string {
  if (nextThing) return nextThing;
  const configured = actions.filter((a) => !isInjected(a));
  const primary = configured.find((a) => a.variant === "primary") ?? configured[0];
  return primary?.label ?? "Dismiss, or tell Claude what to do";
}

/** Verb for toasts and the strip ("Sending", "Archiving", …). */
export function actionVerb(action: Pick<CardAction, "behavior" | "actionKind">): {
  doing: string;
  confirm: string;
  notWired: string;
} {
  if (action.behavior === "default_cleanup" || action.actionKind === "gmail.archive_thread") {
    return { doing: "Archiving", confirm: "Archive", notWired: "Archiving isn't wired up yet" };
  }
  switch (action.actionKind) {
    case "gmail.forward":
      return { doing: "Forwarding", confirm: "Forward", notWired: "Sending isn't wired up yet" };
    case "gmail.label":
      return { doing: "Labelling", confirm: "Label", notWired: "Labelling isn't wired up yet" };
    case "gmail.unarchive_thread":
      return {
        doing: "Moving back to the inbox",
        confirm: "Move back",
        notWired: "Moving mail isn't wired up yet",
      };
    case "gcal.hold":
      return { doing: "Holding", confirm: "Hold", notWired: "Calendar isn't wired up yet" };
    case "gcal.confirm":
      return { doing: "Booking", confirm: "Book", notWired: "Calendar isn't wired up yet" };
    default:
      return { doing: "Sending", confirm: "Send", notWired: "Sending isn't wired up yet" };
  }
}
